import React, { useState, useEffect, useGlobal, useDispatch } from "reactn";
import axios from "axios";
import { Program18 } from "../../assets";
import { FiRefreshCw } from "react-icons/fi";

import BaseModal from "./baseModal/BaseModal";
import { TITLE, SWITCH, IMAGE_DROPDOWN, STRING_INPUT, TEXT_AREA, CONTENT_BLOCK, GET_HTML } from "./baseModal/FieldTypes";
import { notEmptyString } from "./baseModal/validators";
import { toast } from "react-toastify";
import ReshuffleModal from "./ReshuffleModal";

const AIOnlineVirtualInspirationModal = ({ onClose, history }) => {
  const global = useGlobal()[0];
  const setGlobal = useGlobal()[1];
  const [loading, setLoading] = useGlobal("loading");
  const [enrollments, setEnrollments] = useGlobal("enrollments");
  const dispatch = useDispatch();
  
  const [showReshuffleModal, setShowReshuffleModal] = useState(false);
  const [reshuffleMode, setReshuffleMode] = useState("name");
  
  const [superlativeData, setSuperlativeData] = useState({
    mostProfitable: [],
    mostEngaged: [],
    mostUsedSettings: {
      programType: "Recurring Programs",
      targetChildren: true,
      offlinePayments: false,
      applicationRequired: true,
      autopayMandatory: true,
      shareTransactionFee: false
    }
  });

  const [aiSuggestions, setAiSuggestions] = useState({
    suggestedProgramType: "Recurring Programs",
    suggestedSettings: {
      recurringPayments: false,
      offlinePayments: false,
      targetChildren: false,
      applicationRequired: false,
      autopayMandatory: false,
      shareTransactionFee: false
    },
    reasoning: "AI suggestions will be generated based on your virtual program data."
  });

  const [selectedProgramCategory, setSelectedProgramCategory] = useState("Recurring Programs");
  const [addChild, setAddChild] = useState(false);
  const [manualInvoices, setManualInvoices] = useState(false);
  const [applicationRequired, setApplicationRequired] = useState(false);
  const [transactionCost, setTransactionCost] = useState(false);
  const [autoWithdraw, setAutoWithdraw] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);

  const [programName, setProgramName] = useState("Activating magic wizard...");
  const [programDescription, setProgramDescription] = useState("activating magic wizard...");
  const [programImage, setProgramImage] = useState("");

  const [isGeneratingSuggestions, setIsGeneratingSuggestions] = useState(false);
  const [aiAnalysisData, setAiAnalysisData] = useState(null);

  const MAX_IMAGE_SHUFFLES = 3;
  const MAX_GRAND_SHUFFLES = 3;
  const [imageShuffleCount, setImageShuffleCount] = useState(0);
  const [grandShuffleCount, setGrandShuffleCount] = useState(0);
  const [ellipsis, setEllipsis] = useState('.');
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (isGeneratingSuggestions) {
      let i = 0;
      const seq = ['.', '..', '...'];
      const id = setInterval(() => {
        i = (i + 1) % seq.length;
        setEllipsis(seq[i]);
      }, 500);
      return () => clearInterval(id);
    } else {
      setEllipsis('...');
    }
  }, [isGeneratingSuggestions]);

  useEffect(() => {
    if (global.organizationInfo && global.organizationInfo.partnerId) {
      loadAIAnalysis();
      generateAISuggestions(); 
    }
  }, [global.organizationInfo]);

  const loadAIAnalysis = async () => {
    try {
      if (!global.organizationInfo || !global.organizationInfo.partnerId) {
        console.warn("Partner ID not available yet");
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API}/partners/virtual/ai-analysis/${global.organizationInfo.partnerId}`
      );
      
      if (response.data.success) {
        const analysisData = response.data.data;
        updateSuggestionsFromAnalysis(analysisData);
      }
    } catch (error) {
      console.error("Error loading AI analysis:", error);
    }
  };

  const updateSuggestionsFromAnalysis = (analysisData) => {
    if (!analysisData) return;

    if (Array.isArray(analysisData.mostProfitable)) {
      setSuperlativeData(prev => ({
        ...prev,
        mostProfitable: analysisData.mostProfitable
      }));
    }

    if (Array.isArray(analysisData.mostEngaged)) {
      setSuperlativeData(prev => ({
        ...prev,
        mostEngaged: analysisData.mostEngaged
      }));
    }

    if (analysisData.mostUsedSettings) {
      setSuperlativeData(prev => ({
        ...prev,
        mostUsedSettings: {
          programType: analysisData.mostUsedSettings.programType || "Recurring Programs",
          targetChildren: analysisData.mostUsedSettings.targetChildren || true,
          offlinePayments: analysisData.mostUsedSettings.offlinePayments || false,
          applicationRequired: analysisData.mostUsedSettings.applicationRequired || true,
          autopayMandatory: analysisData.mostUsedSettings.autopayMandatory || true,
          shareTransactionFee: analysisData.mostUsedSettings.shareTransactionFee || false
        }
      }));
    }
  };

  const generateAISuggestions = async (constraints = {}) => {
    setIsGeneratingSuggestions(true);
    console.log("Starting AI suggestions generation...");
    console.log("Partner ID:", global.organizationInfo.partnerId);
    
    try {
      const response = await axios.post(`${process.env.REACT_APP_API}/partners/virtual/openai-suggestions`, {
        partnerId: global.organizationInfo.partnerId,
        ...constraints
      });
      
      console.log("API Response:", response);
      console.log("Response data:", response.data);
      
      if (response.data.success) {
        const suggestions = response.data.data;
        console.log("AI Suggestions received:", suggestions);
        
        setProgramName(suggestions.programName);
        setProgramDescription(suggestions.programDescription);
        setProgramImage(suggestions.programImage);
        
        setSelectedProgramCategory(suggestions.programCategory);
        setIsRecurring(suggestions.recurringPayments);
        setManualInvoices(suggestions.offlinePayments);
        setAddChild(suggestions.targetChildren);
        setApplicationRequired(suggestions.applicationRequired);
        setAutoWithdraw(suggestions.autopayMandatory);
        setTransactionCost(suggestions.shareTransactionFee);
        
        setAiSuggestions(prev => ({
          ...prev,
          suggestedProgramType: suggestions.programCategory,
          suggestedSettings: {
            recurringPayments: suggestions.recurringPayments,
            offlinePayments: suggestions.offlinePayments,
            targetChildren: suggestions.targetChildren,
            applicationRequired: suggestions.applicationRequired,
            autopayMandatory: suggestions.autopayMandatory,
            shareTransactionFee: suggestions.shareTransactionFee
          },
          reasoning: suggestions.reasoning
        }));
        
        console.log("AI Suggestions received:", suggestions); 
        toast.success('AI suggestions generated successfully!');
      } else {
        console.error("API returned success=false:", response.data);
        toast.error(response.data.message || 'Failed to generate AI suggestions');
      }
    } catch (error) {
      console.error("Error generating AI suggestions:", error);
      console.error("Error response:", error.response?.data);
      toast.error('Failed to generate AI suggestions. Please try again.');
    } finally {
      setIsGeneratingSuggestions(false);
    }
  };

  const shuffleSuggestions = () => {
    if(grandShuffleCount >= MAX_GRAND_SHUFFLES) {
      toast.info('Grand shuffle limit reached. Please try again later.');
      return;
    }
    setGrandShuffleCount(prev => prev + 1);
    generateAISuggestions({ isShuffleRequest: true });
  };

  const handleImageUpload = async (e) => {
    if (e.target.files.length && e.target.files[0]) {
      const fd = new FormData();
      fd.set("image", e.target.files[0]);
      const ep = `${process.env.REACT_APP_API}/partners/forms/document`;
      setLoading(true);
      try {
        const result = await axios.post(ep, fd);
        if (result.data.success) {
          setProgramImage(result.data.data);
        }
      } catch (error) {
        console.error("Error uploading image:", error);
      }
      setLoading(false);
    }
  };

  const programNameWithShuffle = () => (
    <div style={{ marginBottom: "20px" }}>
      <div style={{ 
        display: "flex", 
        alignItems: "center", 
        gap: "8px", 
        marginBottom: "8px" 
      }}>
        <div className="field-component-title">
          Program Name
        </div>
        <button
          onClick={() => shuffleSpecificField('name')}
          disabled={isGeneratingSuggestions}
          style={{ padding: "4px 8px", backgroundColor: "#6c757d", color: "#fff", border: "none", borderRadius: "8px", cursor: isGeneratingSuggestions ? "not-allowed" : "pointer", fontSize: "11px", whiteSpace: "nowrap", opacity: isGeneratingSuggestions ? 0.6 : 1 }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <FiRefreshCw />
            Shuffle
          </span>
        </button>
      </div>
      <input
        type="text"
        value={
          isGeneratingSuggestions && /^activating magic wizard/i.test(programName)
            ? `Activating magic wizard${ellipsis}`
            : programName
        }
        onChange={e => setProgramName(e.target.value)}
        placeholder="Enter program name"
        style={{
          width: "100%",
          padding: "8px 12px",
          border: "1px solid #ccc",
          borderRadius: "4px",
          fontSize: "14px"
        }}
      />
    </div>
  );

  const programDescriptionWithShuffle = () => (
    <div style={{ marginBottom: "20px" }}>
      <div style={{ 
        display: "flex", 
        alignItems: "center", 
        gap: "8px", 
        marginBottom: "8px" 
      }}>
        <div className="field-component-title">
          Program Description
        </div>
        <button
          onClick={() => shuffleSpecificField('description')}
          disabled={isGeneratingSuggestions}
          style={{ padding: "4px 8px", backgroundColor: "#6c757d", color: "#fff", border: "none", borderRadius: "8px", cursor: isGeneratingSuggestions ? "not-allowed" : "pointer", fontSize: "11px", whiteSpace: "nowrap", opacity: isGeneratingSuggestions ? 0.6 : 1 }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <FiRefreshCw />
            Shuffle
          </span>
        </button>
      </div>
      <textarea
        value={
          isGeneratingSuggestions && /^activating magic wizard/i.test(programDescription)
            ? `activating magic wizard${ellipsis}`
            : programDescription
        }
        onChange={e => setProgramDescription(e.target.value)}
        placeholder="Enter program description"
        style={{
          width: "100%",
          padding: "8px 12px",
          border: "1px solid #ccc",
          borderRadius: "4px",
          fontSize: "14px",
          minHeight: "120px",
          resize: "vertical"
        }}
      />
    </div>
  );

  const programImageWithShuffle = () => (
    <div style={{ marginBottom: "20px" }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: "8px"
      }}>
        <div className="field-component-title">
          Program Image
        </div>
        <button
          onClick={() => shuffleSpecificField('image')}
          disabled={isGeneratingSuggestions || isCreating || imageShuffleCount >= MAX_IMAGE_SHUFFLES}
          style={{
            padding: "4px 8px",
            backgroundColor: "#6c757d",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            cursor: isGeneratingSuggestions || isCreating || imageShuffleCount >= MAX_IMAGE_SHUFFLES ? "not-allowed" : "pointer",
            fontSize: "11px",
            whiteSpace: "nowrap",
            opacity: isGeneratingSuggestions || isCreating || imageShuffleCount >= MAX_IMAGE_SHUFFLES ? 0.6 : 1
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <FiRefreshCw />
            Shuffle
          </span>
        </button>
      </div>
  
      <div
        style={{
          width: "100%",
          minHeight: 200,
          border: "1px solid #e3e3e3",
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          background: "#fafafa"
        }}
      >
        {programImage ? (
          <img
            src={programImage}
            alt="Program preview"
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "cover" }}
          />
        ) : (
          <div style={{ color: "#999", fontSize: 12 }}>
            No image yet. Click Shuffle to generate.
          </div>
        )}
      </div>
    </div>
  );

  const shuffleSpecificField = async (fieldType) => {
    if (fieldType === 'name' || fieldType === 'description') {
      setReshuffleMode(fieldType === 'name' ? 'name' : 'description');
      setShowReshuffleModal(true);
      return;
    }

    if(fieldType === 'image') {
      if(imageShuffleCount >= MAX_IMAGE_SHUFFLES) {
        toast.info('Image shuffle limit reached. Please try again later.');
        return;
      }
      setImageShuffleCount(prev => prev + 1);
    }

    setIsGeneratingSuggestions(true);
    console.log(`Shuffling ${fieldType}...`);
    
    try {
      const response = await axios.post(`${process.env.REACT_APP_API}/partners/virtual/openai-suggestions`, {
        partnerId: global.organizationInfo.partnerId,
        isShuffleRequest: true,
        shuffleType: fieldType
      });
      
      if (response.data.success) {
        const suggestions = response.data.data;
        
        switch (fieldType) {
          case 'description':
            setProgramDescription(suggestions.programDescription);
            toast.success('Program description shuffled!');
            break;
          case 'image':
            setProgramImage(suggestions.programImage);
            toast.success('Program image shuffled!');
            break;
          default:
            break;
        }
      } else {
        toast.error('Failed to shuffle. Please try again.');
      }
    } catch (error) {
      console.error(`Error shuffling ${fieldType}:`, error);
      toast.error('Failed to shuffle. Please try again.');
    } finally {
      setIsGeneratingSuggestions(false);
    }
  };

  const handleApplyGeneratedName = (selectedName) => {
    setProgramName(selectedName);
    toast.success('Program name applied!');
  };

  const handleApplyGeneratedDescription = (selectedDesc) => {
    setProgramDescription(selectedDesc);
    toast.success('Program description applied!');
  };

  const renderTopProfitable = () => (
    <div style={{
      fontSize: "12px",
      color: "#666",
      fontWeight: "normal",
      display: "flex",
      flexDirection: "column",
      gap: "8px"
    }}>
      {superlativeData.mostProfitable.length === 0 ? (
        <div>No data available</div>
      ) : (
        superlativeData.mostProfitable.slice(0, 3).map((p, i) => (
          <div key={`prof-${i}`} style={{ marginBottom: "4px" }}>
            {p.name} | Program Category: {p.category} | Revenue: {p.revenue ? `$${(p.revenue / 100).toLocaleString()}` : "$0"} | Enrollments: {p.enrollment || 0}
          </div>
        ))
      )}
    </div>
  );

  const renderTopEngaged = () => (
    <div style={{
      fontSize: "12px",
      color: "#666",
      fontWeight: "normal",
      display: "flex",
      flexDirection: "column",
      gap: "8px"
    }}>
      {superlativeData.mostEngaged.length === 0 ? (
        <div>No data available</div>
      ) : (
        superlativeData.mostEngaged.slice(0, 3).map((p, i) => (
          <div key={`eng-${i}`} style={{ marginBottom: "4px" }}>
            {p.name} | Program Category: {p.category} | Enrollments: {p.enrollment || 0}
          </div>
        ))
      )}
    </div>
  );

  const handleSubmit = async () => {
    let s3PhotoKey = null;
    setIsCreating(true);
    try {
      if (programImage && /^https?:\/\//i.test(programImage)) {
        const resp = await axios.post(`${process.env.REACT_APP_API}/partners/virtual/import-photo-from-url`, {
          imageUrl: programImage
        });
        if (resp.data?.success && resp.data.data?.program_photo) {
          s3PhotoKey = resp.data.data.program_photo;
        }
      }
    } catch (e) {
      console.error('Failed to import AI image to S3:', e);
    }
    
    setEnrollments([]);
    
 
    setGlobal({
      online: {
        ...global.online,
        program_name: programName,
        description: programDescription,
        program_photo: s3PhotoKey || global.online?.program_photo || null,
        program_category: selectedProgramCategory,
        addChild,
        manual_invoices: manualInvoices,
        application_required: applicationRequired,
        transaction_cost: transactionCost,
        auto_withdraw: autoWithdraw,
        isRecurring
      }
    });

    history.push({
      pathname: "/partners/online-virtual/setup",
      state: {
        selectedProgramCategory: selectedProgramCategory,
        applicationRequired: applicationRequired,
        transactionCost: transactionCost,
        addChild: addChild,
        autoWithdraw: autoWithdraw,
        isRecurring: isRecurring,
        manualInvoices: manualInvoices,
        fromAIModal: true 
      }
    });
    setIsCreating(false);
  };

  const getSuperlativeFields = () => {
    const hasRealData = (superlativeData.mostProfitable || []).length > 0 || (superlativeData.mostEngaged || []).length > 0;
    
    if (!hasRealData) {
      return [
        {
          type: CONTENT_BLOCK,
          data: { 
            name: "Organization Profile",
            content: "Since this is a new organization with no existing virtual programs, AI suggestions will be based on your organizational profile and mission.",
            textStyles: { fontSize: "12px", color: "#666", fontWeight: "normal" }
          }
        }
      ];
    }

    return [
      {
        type: CONTENT_BLOCK,
        data: { 
          name: "Highest Revenue Virtual Program Examples",
          content: "",
          textStyles: { fontSize: "12px", color: "#666", fontWeight: "normal" }
        }
      },
      {
        type: GET_HTML,
        data: { gethtml: renderTopProfitable }
      },
      {
        type: CONTENT_BLOCK,
        data: { 
          name: "Most Engaged Virtual Program Examples",
          content: "",
          textStyles: { fontSize: "12px", color: "#666", fontWeight: "normal" }
        }
      },
      {
        type: GET_HTML,
        data: { gethtml: renderTopEngaged }
      },
      {
        type: CONTENT_BLOCK,
        data: { 
          name: "Most Used Settings",
          content: `Program Type: ${superlativeData.mostUsedSettings.programType} | Target Children: ${superlativeData.mostUsedSettings.targetChildren ? "Yes" : "No"} | Application Required: ${superlativeData.mostUsedSettings.applicationRequired ? "Yes" : "No"} | Offline Payments: ${superlativeData.mostUsedSettings.offlinePayments ? "Yes" : "No"} | Autopay Mandatory: ${superlativeData.mostUsedSettings.autopayMandatory ? "Yes" : "No"} | Share Transaction Fee: ${superlativeData.mostUsedSettings.shareTransactionFee ? "Yes" : "No"}`,
          textStyles: { fontSize: "12px", color: "#666", fontWeight: "normal" }
        }
      }
    ];
  };

  const customTitleFunc = () => {
    return (
      <div style={{
        fontSize: "19px",
        fontWeight: "bold",
        marginBottom: "20px",
        color: "#333",
        fontFamily: "futura-pt, sans-serif"
      }}>
        AI Recommendations (modify as needed):
      </div>
    );
  };

  const getSettingsFields = () => {
    return [
      {
        type: GET_HTML,
        data: {
          gethtml: customTitleFunc
        }
      },
      {
        type: IMAGE_DROPDOWN,
        data: {
          name: "Program Type",
          required: true,
          placeholder: "Pick a type",
          handleChange: value => {
            setSelectedProgramCategory(value);
            if (value === "Single Sessions") {
              setApplicationRequired(true);
              setAutoWithdraw(false);
              setIsRecurring(false);
            }
          },
          choices: [
            { text: "Academics", value: "Academics", key: 1, image: { bordered: true, src: Program18 } },
            { text: "After School", value: "After School", key: 2, image: { bordered: true, src: Program18 } },
            { text: "Arts", value: "Arts", key: 3, image: { bordered: true, src: Program18 } },
            { text: "Athletics", value: "Athletics", key: 4, image: { bordered: true, src: Program18 } },
            { text: "Before & After School", value: "Before & After School", key: 5, image: { bordered: true, src: Program18 } },
            { text: "Camps", value: "Camps", key: 6, image: { bordered: true, src: Program18 } },
            { text: "Cooking", value: "Cooking", key: 7, image: { bordered: true, src: Program18 } },
            { text: "Creative", value: "Creative", key: 8, image: { bordered: true, src: Program18 } },
            { text: "Dance", value: "Dance", key: 9, image: { bordered: true, src: Program18 } },
            { text: "Daycare/Preschool", value: "Daycare/Preschool", key: 10, image: { bordered: true, src: Program18 } },
            { text: "Drama", value: "Drama", key: 11, image: { bordered: true, src: Program18 } },
            { text: "Exercise", value: "Exercise", key: 12, image: { bordered: true, src: Program18 } },
            { text: "Facilities (Rec Centre)", value: "Facilities (Rec Centre)", key: 13, image: { bordered: true, src: Program18 } },
            { text: "Faith", value: "Faith", key: 14, image: { bordered: true, src: Program18 } },
            { text: "Grants Program", value: "Grants Program", key: 15, image: { bordered: true, src: Program18 } },
            { text: "Language/Culture", value: "Language/Culture", key: 16, image: { bordered: true, src: Program18 } },
            { text: "Leadership", value: "Leadership", key: 17, image: { bordered: true, src: Program18 } },
            { text: "Life Skills", value: "Life Skills", key: 18, image: { bordered: true, src: Program18 } },
            { text: "Mixed", value: "Mixed", key: 19, image: { bordered: true, src: Program18 } },
            { text: "Music", value: "Music", key: 20, image: { bordered: true, src: Program18 } },
            { text: "Outdoors", value: "Outdoors", key: 21, image: { bordered: true, src: Program18 } },
            { text: "Playgrounds", value: "Playgrounds", key: 22, image: { bordered: true, src: Program18 } },
            { text: "Service Volunteer", value: "Service Volunteer", key: 23, image: { bordered: true, src: Program18 } },
            { text: "Single Sessions", value: "Single Sessions", key: 24, image: { bordered: true, src: Program18 } },
            { text: "STEM", value: "STEM", key: 25, image: { bordered: true, src: Program18 } },
            { text: "Special Needs", value: "Special Needs", key: 26, image: { bordered: true, src: Program18 } },
            { text: "Theatre", value: "Theatre", key: 27, image: { bordered: true, src: Program18 } },
            { text: "Tutoring", value: "Tutoring", key: 28, image: { bordered: true, src: Program18 } }
          ],
          value: selectedProgramCategory,
          validatorKey: "programType"
        }
      },
      {
        type: SWITCH,
        data: {
          name: "Recurring Payments",
          checked: selectedProgramCategory === "Single Sessions" ? false : isRecurring,
          required: false,
          isDisabled: selectedProgramCategory === "Single Sessions" ? true : false,
          handleClick: () => {
            if (isRecurring) {
              setIsRecurring(false);
              setAutoWithdraw(false);
            } else {
              setIsRecurring(true);
              setAutoWithdraw(!manualInvoices);
            }
          }
        }
      },
      {
        type: SWITCH,
        data: {
          name: "Offline Payments",
          checked: manualInvoices,
          required: false,
          info: "Allows your client to pass through checkout without paying online",
          handleClick: () => {
            setManualInvoices(!manualInvoices);
            setAutoWithdraw(false);
          }
        }
      },
      {
        type: SWITCH,
        data: {
          name: "Target Children",
          checked: addChild,
          required: false,
          handleClick: () => setAddChild(!addChild)
        }
      },
      {
        type: SWITCH,
        data: {
          name: "Application Required",
          checked: applicationRequired,
          required: false,
          handleClick: () => setApplicationRequired(!applicationRequired),
          isDisabled: selectedProgramCategory === "Single Sessions"
        }
      },
      {
        type: SWITCH,
        data: {
          name: "Autopay Mandatory",
          checked: autoWithdraw && isRecurring && !manualInvoices,
          required: false,
          handleClick: () => setAutoWithdraw(!autoWithdraw),
          isDisabled: !isRecurring || manualInvoices,
          info: "Auto withdraw payments from customers for recurring plans. Organizer will be incurring a Credit Card processing fee."
        }
      },
      {
        type: SWITCH,
        data: {
          name: "Share Transaction Fee",
          checked: transactionCost,
          required: false,
          handleClick: () => setTransactionCost(!transactionCost),
          isDisabled: false,
          info: "A small percentage fee will be passed on to customers"
        }
      },
      {
        type: GET_HTML,
        data: {
          gethtml: programNameWithShuffle
        }
      },
      {
        type: GET_HTML,
        data: {
          gethtml: programDescriptionWithShuffle
        }
      },
      {
        type: GET_HTML,
        data: {
          gethtml: programImageWithShuffle
        }
      }
    ];
  };

  const getBaseModalButtons = () => {
    const createButton = {
      name: isCreating ? "Creating..." : "Create",
      handleClick: handleSubmit,
      buttonColour: "#3B4EFF",
      textColour: "#fff",
      validatorKey: "validateSubmit",
      disabled: isCreating || isGeneratingSuggestions
    };

    const shuffleButton = {
      name: isGeneratingSuggestions ? "Shuffling..." : "Grand Shuffle",
      handleClick: shuffleSuggestions,
      buttonColour: "#6c757d",
      textColour: "#fff",
      disabled: isGeneratingSuggestions || isCreating || grandShuffleCount >= MAX_GRAND_SHUFFLES
    };

    return [shuffleButton, createButton];
  };

  const getBaseModalProps = () => {
    return {
      title: "AI Online Virtual Inspiration",
      fields: [...getSuperlativeFields(), ...getSettingsFields()],
      buttons: getBaseModalButtons(),
      handleClose: onClose,
      height: "90vh",
      midSectionHeight: "60vh"
    };
  };

  return (
    <>
      <BaseModal {...getBaseModalProps()} />
      
      {showReshuffleModal && (
        <ReshuffleModal
          onClose={() => setShowReshuffleModal(false)}
          onApply={reshuffleMode === "name" ? handleApplyGeneratedName : handleApplyGeneratedDescription}
          currentValue={reshuffleMode === "name" ? programName : programDescription}
          mode={reshuffleMode}
          listingType="virtual"
        />
      )}
    </>
  );
};

export default AIOnlineVirtualInspirationModal;
